import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import useClickOutside from '../../../Hooks/useClickOutside';
import {
  AdminPageTitle,
  AdminTabButton,
  AdminSearchField,
  AdminTableHeader,
  AdminTableBody,
  AdminSupportingText,
  AdminListFilterDropdown,
  AdminFilterDropdownButton,
  AdminFilterMenuItem,
  AdminToolbarLabel,
  AdminFormSecondaryAction,
  AdminStatusBadge,
  adminFilterDateInputClasses,
  AdminFilterChevron,
  ADMIN_LIST_FILTER_MENU_CLASS,
  ADMIN_TAB_CONTENT_OFFSET_CLASS,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import {
  INITIAL_ACTIVITY,
  INITIAL_SYSTEM_ACTIVITY,
  INITIAL_SECURITY,
  INITIAL_NOTIFICATIONS,
  INITIAL_INTEGRATIONS,
  INITIAL_API,
  INITIAL_HEALTH,
  buildLogFilterOptions,
} from '../../../data/systemLogsSeedData';
import { formatChangedBy, formatModuleDisplay } from '../../../data/changeHistoryEntry';

const PAGE_SIZE = 10;

const TAB_ACTIVITY = 'activity';
/** Destination id remains `processing` so existing links keep working; displayed label is System Activity. */
const TAB_SYSTEM_ACTIVITY = 'processing';
const TAB_SECURITY = 'security';
const TAB_NOTIFICATIONS = 'notifications';
const TAB_INTEGRATIONS = 'integrations';
const TAB_API = 'api';
const TAB_HEALTH = 'health';

/** Legacy alias → System Activity destination. */
const TAB_ALIASES = {
  'system-activity': TAB_SYSTEM_ACTIVITY,
  'system_activity': TAB_SYSTEM_ACTIVITY,
  processing: TAB_SYSTEM_ACTIVITY,
};

const TAB_ORDER = [
  { id: TAB_ACTIVITY, label: 'Admin Activity' },
  { id: TAB_SYSTEM_ACTIVITY, label: 'System Activity' },
  { id: TAB_SECURITY, label: 'Security' },
  { id: TAB_NOTIFICATIONS, label: 'Notifications' },
  { id: TAB_INTEGRATIONS, label: 'Integrations' },
  { id: TAB_API, label: 'API' },
  { id: TAB_HEALTH, label: 'Health & Errors' },
];

const VALID_TAB_IDS = TAB_ORDER.map((t) => t.id);

const resolveTabId = (raw) => {
  if (!raw) return TAB_ACTIVITY;
  const key = String(raw).trim().toLowerCase();
  if (TAB_ALIASES[key]) return TAB_ALIASES[key];
  if (VALID_TAB_IDS.includes(raw)) return raw;
  if (VALID_TAB_IDS.includes(key)) return key;
  return TAB_ACTIVITY;
};

const DEFAULT_ACTIVITY_FILTERS = { source: 'All', actionBy: 'All', fromDate: '', toDate: '' };
const DEFAULT_SYSTEM_ACTIVITY_FILTERS = { module: 'All', fromDate: '', toDate: '' };
const DEFAULT_SECURITY_FILTERS = { source: 'All', result: 'All', fromDate: '', toDate: '' };
const DEFAULT_NOTIFICATION_FILTERS = { deliveryMethod: 'All', result: 'All', fromDate: '', toDate: '' };
const DEFAULT_INTEGRATION_FILTERS = { event: 'All', result: 'All', fromDate: '', toDate: '' };
const DEFAULT_API_FILTERS = { service: 'All', result: 'All', fromDate: '', toDate: '' };
const DEFAULT_HEALTH_FILTERS = { source: 'All', result: 'All', fromDate: '', toDate: '' };

const matchesSearch = (row, searchFields, query) => {
  const q = query.toLowerCase().trim();
  if (!q) return true;
  const str = (v) => (v == null ? '' : String(v).toLowerCase());
  return searchFields.some((field) => str(row[field]).includes(q));
};

const isInDateRange = (value, fromDate, toDate) => {
  if (!fromDate && !toDate) return true;
  if (!value) return false;
  if (fromDate && value < fromDate) return false;
  if (toDate && value > toDate) return false;
  return true;
};

const formatFilterDateLabel = (iso) => {
  if (!iso) return null;
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDateRangeLabel = (fromDate, toDate) => {
  if (!fromDate && !toDate) return 'All';
  const from = formatFilterDateLabel(fromDate);
  const to = formatFilterDateLabel(toDate);
  if (from && to) return `${from} – ${to}`;
  if (from) return `From ${from}`;
  return `To ${to}`;
};

const sortNewestLoggedOnFirst = (rows) =>
  [...rows].sort((a, b) => {
    const first = a.loggedOnInstant || a.loggedOnDate || a.loggedOn || '';
    const second = b.loggedOnInstant || b.loggedOnDate || b.loggedOn || '';
    return String(second).localeCompare(String(first));
  });

/** Logged On — same date/time format as Customers screen 1 (Last Active). */
const formatLoggedOnDisplay = (row) => {
  const raw = row?.loggedOnInstant || row?.loggedOn;
  if (!raw) return '—';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) {
    return String(row?.loggedOn || '')
      .replace(/\s*UTC\s*$/i, '')
      .trim() || '—';
  }
  return (
    date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) +
    ', ' +
    date.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true })
  );
};

const formatActionByDisplay = (actionBy) => formatChangedBy(actionBy);

function LogFilterSelect({
  filterKey,
  openFilterKey,
  setOpenFilterKey,
  label,
  value,
  options,
  onSelect,
}) {
  return (
    <AdminListFilterDropdown
      label={label}
      value={value}
      options={options.map((option) => ({
        value: option,
        label: option === 'All' ? 'All' : option,
      }))}
      onChange={onSelect}
      open={openFilterKey === filterKey}
      setOpen={(next) => setOpenFilterKey(next ? filterKey : null)}
    />
  );
}

function LogFilterDateRange({
  filterKey,
  openFilterKey,
  setOpenFilterKey,
  fromDate,
  toDate,
  onFromChange,
  onToChange,
  label = 'Date Range',
}) {
  const isOpen = openFilterKey === filterKey;
  const hasValue = Boolean(fromDate || toDate);

  return (
    <div className="relative">
      <AdminFilterDropdownButton
        type="button"
        onClick={() => setOpenFilterKey(isOpen ? null : filterKey)}
      >
        <span className="truncate">
          {hasValue ? formatDateRangeLabel(fromDate, toDate) : label}
        </span>
        <AdminFilterChevron open={isOpen} />
      </AdminFilterDropdownButton>
      {isOpen && (
        <div className={`${ADMIN_LIST_FILTER_MENU_CLASS} !max-w-[18rem] px-3 py-2 space-y-2`}>
          <div>
            <AdminToolbarLabel className="block mb-1">From</AdminToolbarLabel>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => onFromChange(e.target.value)}
              className={adminFilterDateInputClasses('!w-full')}
              aria-label="From date"
            />
          </div>
          <div>
            <AdminToolbarLabel className="block mb-1">To</AdminToolbarLabel>
            <input
              type="date"
              value={toDate}
              onChange={(e) => onToChange(e.target.value)}
              className={adminFilterDateInputClasses('!w-full')}
              aria-label="To date"
            />
          </div>
          {hasValue ? (
            <AdminFilterMenuItem
              active={false}
              onClick={() => {
                onFromChange('');
                onToChange('');
                setOpenFilterKey(null);
              }}
            >
              Clear
            </AdminFilterMenuItem>
          ) : null}
        </div>
      )}
    </div>
  );
}

function LogTable({ minWidth, columns, headers, rows, renderCells }) {
  return (
    <div className={`w-full ${minWidth}`}>
      <div
        className="grid w-full py-3 text-offblack border-b border-lightgray px-4 gap-x-3 gap-y-2"
        style={{ gridTemplateColumns: columns }}
      >
        {headers.map((header) => (
          <div key={header} className="flex items-center min-w-0">
            <AdminTableHeader className="break-words">{header}</AdminTableHeader>
          </div>
        ))}
      </div>
      {rows.map((row) => (
        <div
          key={row.id}
          className="grid w-full py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-3 gap-y-2"
          style={{ gridTemplateColumns: columns }}
        >
          {renderCells(row).map((value, index) => {
            const isPlainText =
              value == null || typeof value === 'string' || typeof value === 'number';
            return (
              <div
                key={`${row.id}-${headers[index]}`}
                className="flex items-start min-w-0 min-h-8"
                title={isPlainText && value != null ? String(value) : undefined}
              >
                {isPlainText ? (
                  <AdminTableBody className="break-words block whitespace-normal">
                    {value}
                  </AdminTableBody>
                ) : (
                  value
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

const SystemLogs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = resolveTabId(searchParams.get('tab'));
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  const [searchByTab, setSearchByTab] = useState({
    [TAB_ACTIVITY]: '',
    [TAB_SYSTEM_ACTIVITY]: '',
    [TAB_SECURITY]: '',
    [TAB_NOTIFICATIONS]: '',
    [TAB_INTEGRATIONS]: '',
    [TAB_API]: '',
    [TAB_HEALTH]: '',
  });
  const searchQuery = searchByTab[activeTab] || '';
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [activityLogs] = useState(INITIAL_ACTIVITY);
  const [systemActivityLogs] = useState(INITIAL_SYSTEM_ACTIVITY);
  const [securityLogs] = useState(INITIAL_SECURITY);
  const [notificationLogs] = useState(INITIAL_NOTIFICATIONS);
  const [integrationsLogs] = useState(INITIAL_INTEGRATIONS);
  const [apiLogs] = useState(INITIAL_API);
  const [healthLogs] = useState(INITIAL_HEALTH);

  const ACTIVITY_SOURCES = useMemo(() => buildLogFilterOptions(activityLogs, 'source'), [activityLogs]);
  const ACTIVITY_ACTION_BY = useMemo(() => {
    const values = [
      ...new Set(
        (activityLogs || [])
          .map((row) => formatActionByDisplay(row.actionBy))
          .filter((value) => value != null && String(value).trim() !== ''),
      ),
    ].sort((a, b) => String(a).localeCompare(String(b)));
    return ['All', ...values];
  }, [activityLogs]);
  const SYSTEM_ACTIVITY_MODULES = useMemo(() => {
    const values = [
      ...new Set(
        (systemActivityLogs || [])
          .map((row) => formatModuleDisplay(row.module))
          .filter((value) => value != null && String(value).trim() !== ''),
      ),
    ].sort((a, b) => String(a).localeCompare(String(b)));
    return ['All', ...values];
  }, [systemActivityLogs]);
  const SECURITY_SOURCES = useMemo(() => {
    const values = [
      ...new Set(
        (securityLogs || [])
          .map((row) => formatModuleDisplay(row.source))
          .filter((value) => value != null && String(value).trim() !== ''),
      ),
    ].sort((a, b) => String(a).localeCompare(String(b)));
    return ['All', ...values];
  }, [securityLogs]);
  const SECURITY_RESULTS = useMemo(() => buildLogFilterOptions(securityLogs, 'result'), [securityLogs]);
  const NOTIFICATION_METHODS = useMemo(
    () => buildLogFilterOptions(notificationLogs, 'deliveryMethod'),
    [notificationLogs],
  );
  const NOTIFICATION_RESULTS = useMemo(
    () => buildLogFilterOptions(notificationLogs, 'result'),
    [notificationLogs],
  );
  const INTEGRATION_EVENTS = useMemo(
    () => buildLogFilterOptions(integrationsLogs, 'event'),
    [integrationsLogs],
  );
  const INTEGRATION_RESULTS = useMemo(
    () => buildLogFilterOptions(integrationsLogs, 'result'),
    [integrationsLogs],
  );
  const API_SERVICES = useMemo(() => buildLogFilterOptions(apiLogs, 'service'), [apiLogs]);
  const API_RESULTS = useMemo(() => buildLogFilterOptions(apiLogs, 'result'), [apiLogs]);
  const HEALTH_SOURCES = useMemo(() => buildLogFilterOptions(healthLogs, 'source'), [healthLogs]);
  const HEALTH_RESULTS = useMemo(() => buildLogFilterOptions(healthLogs, 'result'), [healthLogs]);

  const [activityFilters, setActivityFilters] = useState(DEFAULT_ACTIVITY_FILTERS);
  const [systemActivityFilters, setSystemActivityFilters] = useState(DEFAULT_SYSTEM_ACTIVITY_FILTERS);
  const [securityFilters, setSecurityFilters] = useState(DEFAULT_SECURITY_FILTERS);
  const [notificationFilters, setNotificationFilters] = useState(DEFAULT_NOTIFICATION_FILTERS);
  const [integrationFilters, setIntegrationFilters] = useState(DEFAULT_INTEGRATION_FILTERS);
  const [apiFilters, setApiFilters] = useState(DEFAULT_API_FILTERS);
  const [healthFilters, setHealthFilters] = useState(DEFAULT_HEALTH_FILTERS);
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const filtersRef = useRef(null);

  useClickOutside(filtersRef, () => setOpenFilterKey(null));

  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    const timer = window.setTimeout(() => setLoading(false), 280);
    return () => window.clearTimeout(timer);
  }, [activeTab]);

  useEffect(() => {
    const t = resolveTabId(searchParams.get('tab'));
    setActiveTab(t);

    const service = searchParams.get('service') || searchParams.get('provider') || searchParams.get('integration') || '';
    const environment = searchParams.get('environment') || '';
    const eventType = searchParams.get('event') || searchParams.get('eventType') || '';
    const correlationId = searchParams.get('correlationId') || '';
    const fromDate = searchParams.get('from') || searchParams.get('fromDate') || '';
    const toDate = searchParams.get('to') || searchParams.get('toDate') || '';

    if (service || environment || eventType || correlationId || fromDate || toDate) {
      setIntegrationFilters((prev) => ({
        ...prev,
        event: eventType
          ? (INTEGRATION_EVENTS.find((name) => name !== 'All' && name.toLowerCase().includes(eventType.toLowerCase())) || prev.event)
          : prev.event,
        fromDate: fromDate || prev.fromDate,
        toDate: toDate || prev.toDate,
      }));
      if (correlationId || service) {
        setSearchByTab((prev) => ({
          ...prev,
          [TAB_INTEGRATIONS]: prev[TAB_INTEGRATIONS] || correlationId || service,
        }));
      }
    }

    const ruleId = searchParams.get('ruleId') || searchParams.get('notificationRuleId') || '';
    if (ruleId) {
      if (t === TAB_NOTIFICATIONS || searchParams.get('notificationRuleId')) {
        setSearchByTab((prev) => ({ ...prev, [TAB_NOTIFICATIONS]: prev[TAB_NOTIFICATIONS] || ruleId }));
      } else {
        setSearchByTab((prev) => ({ ...prev, [TAB_SYSTEM_ACTIVITY]: prev[TAB_SYSTEM_ACTIVITY] || ruleId }));
      }
    }
  }, [searchParams]);

  useEffect(() => {
    setCurrentPage(1);
    setOpenFilterKey(null);
  }, [
    activeTab,
    searchQuery,
    activityFilters,
    systemActivityFilters,
    securityFilters,
    notificationFilters,
    integrationFilters,
    apiFilters,
    healthFilters,
  ]);

  const setTab = (tab) => {
    const resolved = resolveTabId(tab);
    if (!VALID_TAB_IDS.includes(resolved)) return;
    setActiveTab(resolved);
    const next = new URLSearchParams(searchParams);
    next.set('tab', resolved);
    setSearchParams(next);
  };

  const retryLoading = () => {
    setLoadError(false);
    setLoading(true);
    window.setTimeout(() => setLoading(false), 400);
  };

  const filteredActivity = useMemo(
    () =>
      sortNewestLoggedOnFirst(
        activityLogs.filter((row) => {
          if (
            !matchesSearch(
              row,
              ['source', 'action', 'actionBy', 'environment', 'service', 'result', 'referenceId', 'details', 'loggedOn'],
              searchQuery,
            )
          ) {
            return false;
          }
          if (activityFilters.source !== 'All' && row.source !== activityFilters.source) return false;
          if (
            activityFilters.actionBy !== 'All' &&
            formatActionByDisplay(row.actionBy) !== activityFilters.actionBy
          ) {
            return false;
          }
          if (!isInDateRange(row.loggedOnDate, activityFilters.fromDate, activityFilters.toDate)) return false;
          return true;
        }),
      ),
    [activityLogs, searchQuery, activityFilters],
  );

  const filteredSystemActivity = useMemo(
    () =>
      sortNewestLoggedOnFirst(
        systemActivityLogs.filter((row) => {
          if (!matchesSearch(row, ['module', 'process', 'recordId', 'ruleId', 'runId', 'occurrenceId', 'attempt', 'result', 'details', 'loggedOn'], searchQuery)) return false;
          if (
            systemActivityFilters.module !== 'All' &&
            formatModuleDisplay(row.module) !== systemActivityFilters.module
          ) {
            return false;
          }
          if (!isInDateRange(row.loggedOnDate, systemActivityFilters.fromDate, systemActivityFilters.toDate)) return false;
          return true;
        }),
      ),
    [systemActivityLogs, searchQuery, systemActivityFilters],
  );

  const filteredSecurity = useMemo(
    () =>
      securityLogs.filter((row) => {
        if (!matchesSearch(row, ['source', 'event', 'affectedUser', 'environment', 'result', 'referenceId', 'details', 'loggedOn'], searchQuery)) return false;
        if (
          securityFilters.source !== 'All' &&
          formatModuleDisplay(row.source) !== securityFilters.source
        ) {
          return false;
        }
        if (securityFilters.result !== 'All' && row.result !== securityFilters.result) return false;
        if (!isInDateRange(row.loggedOnDate, securityFilters.fromDate, securityFilters.toDate)) return false;
        return true;
      }),
    [securityLogs, searchQuery, securityFilters],
  );

  const filteredNotifications = useMemo(
    () =>
      notificationLogs.filter((row) => {
        if (!matchesSearch(row, ['eventOccurrenceId', 'notificationRuleId', 'notificationId', 'deliveryMethod', 'scheduledSendNumber', 'deliveryAttempt', 'result', 'details', 'loggedOn'], searchQuery)) return false;
        if (notificationFilters.deliveryMethod !== 'All' && row.deliveryMethod !== notificationFilters.deliveryMethod) return false;
        if (notificationFilters.result !== 'All' && row.result !== notificationFilters.result) return false;
        if (!isInDateRange(row.loggedOnDate, notificationFilters.fromDate, notificationFilters.toDate)) return false;
        return true;
      }),
    [notificationLogs, searchQuery, notificationFilters],
  );

  const filteredIntegrations = useMemo(
    () =>
      integrationsLogs.filter((row) => {
        if (!matchesSearch(row, ['service', 'event', 'environment', 'correlationId', 'requestReference', 'providerReference', 'result', 'details', 'loggedOn'], searchQuery)) return false;
        if (integrationFilters.event !== 'All' && row.event !== integrationFilters.event) return false;
        if (integrationFilters.result !== 'All' && row.result !== integrationFilters.result) return false;
        if (!isInDateRange(row.loggedOnDate, integrationFilters.fromDate, integrationFilters.toDate)) return false;
        return true;
      }),
    [integrationsLogs, searchQuery, integrationFilters],
  );

  const filteredApi = useMemo(
    () =>
      apiLogs.filter((row) => {
        if (!matchesSearch(row, ['service', 'endpoint', 'environment', 'correlationId', 'requestReference', 'providerReference', 'result', 'details', 'loggedOn'], searchQuery)) return false;
        if (apiFilters.service !== 'All' && row.service !== apiFilters.service) return false;
        if (apiFilters.result !== 'All' && row.result !== apiFilters.result) return false;
        if (!isInDateRange(row.loggedOnDate, apiFilters.fromDate, apiFilters.toDate)) return false;
        return true;
      }),
    [apiLogs, searchQuery, apiFilters],
  );

  const filteredHealth = useMemo(
    () =>
      healthLogs.filter((row) => {
        if (!matchesSearch(row, ['source', 'component', 'fault', 'environment', 'referenceId', 'result', 'details', 'loggedOn'], searchQuery)) return false;
        if (healthFilters.source !== 'All' && row.source !== healthFilters.source) return false;
        if (healthFilters.result !== 'All' && row.result !== healthFilters.result) return false;
        if (!isInDateRange(row.loggedOnDate, healthFilters.fromDate, healthFilters.toDate)) return false;
        return true;
      }),
    [healthLogs, searchQuery, healthFilters],
  );

  const filteredByTab = {
    [TAB_ACTIVITY]: filteredActivity,
    [TAB_SYSTEM_ACTIVITY]: filteredSystemActivity,
    [TAB_SECURITY]: filteredSecurity,
    [TAB_NOTIFICATIONS]: filteredNotifications,
    [TAB_INTEGRATIONS]: filteredIntegrations,
    [TAB_API]: filteredApi,
    [TAB_HEALTH]: filteredHealth,
  };

  const sourceCountByTab = {
    [TAB_ACTIVITY]: activityLogs.length,
    [TAB_SYSTEM_ACTIVITY]: systemActivityLogs.length,
    [TAB_SECURITY]: securityLogs.length,
    [TAB_NOTIFICATIONS]: notificationLogs.length,
    [TAB_INTEGRATIONS]: integrationsLogs.length,
    [TAB_API]: apiLogs.length,
    [TAB_HEALTH]: healthLogs.length,
  };

  const filteredList = filteredByTab[activeTab] || [];
  const total = filteredList.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const start = (currentPage - 1) * PAGE_SIZE;
  const pageList = filteredList.slice(start, start + PAGE_SIZE);
  const rangeStart = total === 0 ? 0 : start + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, total);
  const showPagination = total > PAGE_SIZE;

  const searchPlaceholder = 'Search displayed values in this tab';

  const activityGrid =
    'minmax(110px, 0.85fr) minmax(110px, 0.8fr) minmax(140px, 1.1fr) minmax(110px, 0.85fr) minmax(110px, 0.85fr) minmax(90px, 0.7fr) minmax(90px, 0.65fr) minmax(130px, 0.95fr) minmax(130px, 0.9fr)';
  const systemActivityGrid =
    'minmax(90px, 0.7fr) minmax(90px, 0.7fr) minmax(120px, 0.9fr) minmax(140px, 1.1fr) minmax(120px, 0.9fr) minmax(70px, 0.5fr) minmax(100px, 0.75fr) minmax(110px, 0.85fr) minmax(90px, 0.7fr) minmax(130px, 0.9fr)';
  const securityGrid =
    'minmax(100px, 0.8fr) minmax(130px, 1fr) minmax(150px, 1.15fr) minmax(120px, 0.95fr) minmax(140px, 1.05fr) minmax(90px, 0.7fr) minmax(90px, 0.65fr) minmax(130px, 0.9fr)';
  const notificationsGrid =
    'minmax(110px, 0.85fr) minmax(90px, 0.7fr) minmax(100px, 0.75fr) minmax(140px, 1.1fr) minmax(90px, 0.65fr) minmax(80px, 0.55fr) minmax(110px, 0.85fr) minmax(100px, 0.75fr) minmax(130px, 0.9fr)';
  const integrationsGrid =
    'minmax(110px, 0.85fr) minmax(110px, 0.85fr) minmax(100px, 0.75fr) minmax(140px, 1.1fr) minmax(110px, 0.85fr) minmax(110px, 0.85fr) minmax(90px, 0.7fr) minmax(90px, 0.7fr) minmax(130px, 0.9fr)';
  const apiGrid =
    'minmax(110px, 0.85fr) minmax(110px, 0.85fr) minmax(140px, 1.1fr) minmax(120px, 0.95fr) minmax(110px, 0.85fr) minmax(110px, 0.85fr) minmax(90px, 0.7fr) minmax(90px, 0.7fr) minmax(130px, 0.9fr)';
  const healthGrid =
    'minmax(100px, 0.8fr) minmax(120px, 0.95fr) minmax(130px, 1fr) minmax(150px, 1.15fr) minmax(110px, 0.85fr) minmax(90px, 0.7fr) minmax(90px, 0.7fr) minmax(130px, 0.9fr)';

  const emptyMessage =
    sourceCountByTab[activeTab] === 0
      ? 'No System Log records are available in this tab.'
      : 'No System Log records match the current search and filters.';

  return (
    <AdminLayout>
      <div>
        <AdminPageTitle>System Logs</AdminPageTitle>

        <div className={`${ADMIN_TOOLBAR_CLASS} overflow-visible`}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            <div className="w-full max-w-[450px]">
              <AdminSearchField
                type="text"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) =>
                  setSearchByTab((prev) => ({ ...prev, [activeTab]: e.target.value }))
                }
              />
            </div>

            <div
              ref={filtersRef}
              className="flex flex-wrap gap-3 w-full lg:w-auto overflow-visible relative z-20 items-center"
            >
              {activeTab === TAB_ACTIVITY && (
                <>
                  <LogFilterSelect filterKey="activity-source" openFilterKey={openFilterKey} setOpenFilterKey={setOpenFilterKey} label="Source" value={activityFilters.source} options={ACTIVITY_SOURCES} onSelect={(source) => setActivityFilters((prev) => ({ ...prev, source }))} />
                  <LogFilterSelect filterKey="activity-action-by" openFilterKey={openFilterKey} setOpenFilterKey={setOpenFilterKey} label="Action By" value={activityFilters.actionBy} options={ACTIVITY_ACTION_BY} onSelect={(actionBy) => setActivityFilters((prev) => ({ ...prev, actionBy }))} />
                  <LogFilterDateRange filterKey="activity-date" openFilterKey={openFilterKey} setOpenFilterKey={setOpenFilterKey} fromDate={activityFilters.fromDate} toDate={activityFilters.toDate} onFromChange={(fromDate) => setActivityFilters((prev) => ({ ...prev, fromDate }))} onToChange={(toDate) => setActivityFilters((prev) => ({ ...prev, toDate }))} />
                </>
              )}

              {activeTab === TAB_SYSTEM_ACTIVITY && (
                <>
                  <LogFilterSelect filterKey="system-activity-module" openFilterKey={openFilterKey} setOpenFilterKey={setOpenFilterKey} label="Module" value={systemActivityFilters.module} options={SYSTEM_ACTIVITY_MODULES} onSelect={(module) => setSystemActivityFilters((prev) => ({ ...prev, module }))} />
                  <LogFilterDateRange filterKey="system-activity-date" openFilterKey={openFilterKey} setOpenFilterKey={setOpenFilterKey} fromDate={systemActivityFilters.fromDate} toDate={systemActivityFilters.toDate} onFromChange={(fromDate) => setSystemActivityFilters((prev) => ({ ...prev, fromDate }))} onToChange={(toDate) => setSystemActivityFilters((prev) => ({ ...prev, toDate }))} />
                </>
              )}

              {activeTab === TAB_SECURITY && (
                <>
                  <LogFilterSelect filterKey="security-source" openFilterKey={openFilterKey} setOpenFilterKey={setOpenFilterKey} label="Source" value={securityFilters.source} options={SECURITY_SOURCES} onSelect={(source) => setSecurityFilters((prev) => ({ ...prev, source }))} />
                  <LogFilterSelect filterKey="security-result" openFilterKey={openFilterKey} setOpenFilterKey={setOpenFilterKey} label="Result" value={securityFilters.result} options={SECURITY_RESULTS} onSelect={(result) => setSecurityFilters((prev) => ({ ...prev, result }))} />
                  <LogFilterDateRange filterKey="security-date" openFilterKey={openFilterKey} setOpenFilterKey={setOpenFilterKey} fromDate={securityFilters.fromDate} toDate={securityFilters.toDate} onFromChange={(fromDate) => setSecurityFilters((prev) => ({ ...prev, fromDate }))} onToChange={(toDate) => setSecurityFilters((prev) => ({ ...prev, toDate }))} />
                </>
              )}

              {activeTab === TAB_NOTIFICATIONS && (
                <>
                  <LogFilterSelect filterKey="notifications-method" openFilterKey={openFilterKey} setOpenFilterKey={setOpenFilterKey} label="Delivery Method" value={notificationFilters.deliveryMethod} options={NOTIFICATION_METHODS} onSelect={(deliveryMethod) => setNotificationFilters((prev) => ({ ...prev, deliveryMethod }))} />
                  <LogFilterSelect filterKey="notifications-result" openFilterKey={openFilterKey} setOpenFilterKey={setOpenFilterKey} label="Result" value={notificationFilters.result} options={NOTIFICATION_RESULTS} onSelect={(result) => setNotificationFilters((prev) => ({ ...prev, result }))} />
                  <LogFilterDateRange filterKey="notifications-date" openFilterKey={openFilterKey} setOpenFilterKey={setOpenFilterKey} fromDate={notificationFilters.fromDate} toDate={notificationFilters.toDate} onFromChange={(fromDate) => setNotificationFilters((prev) => ({ ...prev, fromDate }))} onToChange={(toDate) => setNotificationFilters((prev) => ({ ...prev, toDate }))} />
                </>
              )}

              {activeTab === TAB_INTEGRATIONS && (
                <>
                  <LogFilterSelect filterKey="integrations-event" openFilterKey={openFilterKey} setOpenFilterKey={setOpenFilterKey} label="Event" value={integrationFilters.event} options={INTEGRATION_EVENTS} onSelect={(event) => setIntegrationFilters((prev) => ({ ...prev, event }))} />
                  <LogFilterSelect filterKey="integrations-result" openFilterKey={openFilterKey} setOpenFilterKey={setOpenFilterKey} label="Result" value={integrationFilters.result} options={INTEGRATION_RESULTS} onSelect={(result) => setIntegrationFilters((prev) => ({ ...prev, result }))} />
                  <LogFilterDateRange filterKey="integrations-date" openFilterKey={openFilterKey} setOpenFilterKey={setOpenFilterKey} fromDate={integrationFilters.fromDate} toDate={integrationFilters.toDate} onFromChange={(fromDate) => setIntegrationFilters((prev) => ({ ...prev, fromDate }))} onToChange={(toDate) => setIntegrationFilters((prev) => ({ ...prev, toDate }))} />
                </>
              )}

              {activeTab === TAB_API && (
                <>
                  <LogFilterSelect filterKey="api-service" openFilterKey={openFilterKey} setOpenFilterKey={setOpenFilterKey} label="Service" value={apiFilters.service} options={API_SERVICES} onSelect={(service) => setApiFilters((prev) => ({ ...prev, service }))} />
                  <LogFilterSelect filterKey="api-result" openFilterKey={openFilterKey} setOpenFilterKey={setOpenFilterKey} label="Result" value={apiFilters.result} options={API_RESULTS} onSelect={(result) => setApiFilters((prev) => ({ ...prev, result }))} />
                  <LogFilterDateRange filterKey="api-date" openFilterKey={openFilterKey} setOpenFilterKey={setOpenFilterKey} fromDate={apiFilters.fromDate} toDate={apiFilters.toDate} onFromChange={(fromDate) => setApiFilters((prev) => ({ ...prev, fromDate }))} onToChange={(toDate) => setApiFilters((prev) => ({ ...prev, toDate }))} />
                </>
              )}

              {activeTab === TAB_HEALTH && (
                <>
                  <LogFilterSelect filterKey="health-source" openFilterKey={openFilterKey} setOpenFilterKey={setOpenFilterKey} label="Source" value={healthFilters.source} options={HEALTH_SOURCES} onSelect={(source) => setHealthFilters((prev) => ({ ...prev, source }))} />
                  <LogFilterSelect filterKey="health-result" openFilterKey={openFilterKey} setOpenFilterKey={setOpenFilterKey} label="Result" value={healthFilters.result} options={HEALTH_RESULTS} onSelect={(result) => setHealthFilters((prev) => ({ ...prev, result }))} />
                  <LogFilterDateRange filterKey="health-date" openFilterKey={openFilterKey} setOpenFilterKey={setOpenFilterKey} fromDate={healthFilters.fromDate} toDate={healthFilters.toDate} onFromChange={(fromDate) => setHealthFilters((prev) => ({ ...prev, fromDate }))} onToChange={(toDate) => setHealthFilters((prev) => ({ ...prev, toDate }))} />
                </>
              )}
            </div>
          </div>
        </div>

        <DashboardMainContainer className={`${ADMIN_TABLE_CLASS} max-w-full min-w-0`}>
          <div className="border-b border-lightgray">
            <div className="flex space-x-8 overflow-x-auto">
              {TAB_ORDER.map((tab) => (
                <AdminTabButton
                  key={tab.id}
                  active={activeTab === tab.id}
                  onClick={() => setTab(tab.id)}
                >
                  {tab.label}
                </AdminTabButton>
              ))}
            </div>
          </div>
          <div className={`${ADMIN_TAB_CONTENT_OFFSET_CLASS} w-full min-w-0`}>
            <div className="w-full overflow-x-auto">
              {loading ? (
                <div className="py-10 text-center">
                  <AdminSupportingText className="text-mediumgray">Loading…</AdminSupportingText>
                </div>
              ) : loadError ? (
                <div className="py-10 text-center space-y-3">
                  <AdminSupportingText className="text-mediumgray">
                    System Logs could not be loaded. Try again.
                  </AdminSupportingText>
                  <AdminFormSecondaryAction type="button" onClick={retryLoading}>
                    Retry Loading
                  </AdminFormSecondaryAction>
                </div>
              ) : (
                <>
                  {activeTab === TAB_ACTIVITY && (
                    <LogTable
                      minWidth="min-w-[1280px]"
                      columns={activityGrid}
                      headers={[
                        'Reference ID',
                        'Source',
                        'Details',
                        'Action',
                        'Service',
                        'Environment',
                        'Result',
                        'Action By',
                        'Logged On',
                      ]}
                      rows={pageList}
                      renderCells={(row) => [
                        row.referenceId,
                        row.source,
                        row.details,
                        row.action,
                        row.service,
                        row.environment,
                        <AdminStatusBadge key={`${row.id}-result`} status={row.result} label={row.result} />,
                        formatActionByDisplay(row.actionBy),
                        formatLoggedOnDisplay(row),
                      ]}
                    />
                  )}

                  {activeTab === TAB_SYSTEM_ACTIVITY && (
                    <LogTable
                      minWidth="min-w-[1400px]"
                      columns={systemActivityGrid}
                      headers={[
                        'Rule ID',
                        'Record ID',
                        'Module',
                        'Details',
                        'Process',
                        'Attempt',
                        'Run ID',
                        'Occurrence ID',
                        'Result',
                        'Logged On',
                      ]}
                      rows={pageList}
                      renderCells={(row) => [
                        row.ruleId,
                        row.recordId,
                        formatModuleDisplay(row.module),
                        row.details,
                        row.process,
                        row.attempt,
                        row.runId,
                        row.occurrenceId,
                        <AdminStatusBadge key={`${row.id}-result`} status={row.result} label={row.result} />,
                        formatLoggedOnDisplay(row),
                      ]}
                    />
                  )}

                  {activeTab === TAB_SECURITY && (
                    <LogTable
                      minWidth="min-w-[1200px]"
                      columns={securityGrid}
                      headers={[
                        'Reference ID',
                        'Source',
                        'Details',
                        'Event',
                        'Affected User',
                        'Environment',
                        'Result',
                        'Logged On',
                      ]}
                      rows={pageList}
                      renderCells={(row) => [
                        row.referenceId,
                        formatModuleDisplay(row.source),
                        row.details,
                        row.event,
                        row.affectedUser,
                        row.environment,
                        <AdminStatusBadge key={`${row.id}-result`} status={row.result} label={row.result} />,
                        formatLoggedOnDisplay(row),
                      ]}
                    />
                  )}

                  {activeTab === TAB_NOTIFICATIONS && (
                    <LogTable
                      minWidth="min-w-[1280px]"
                      columns={notificationsGrid}
                      headers={[
                        'Notification ID',
                        'Rule ID',
                        'Delivery Method',
                        'Details',
                        'Schd Send',
                        'Attempt',
                        'Event ID',
                        'Result',
                        'Logged On',
                      ]}
                      rows={pageList}
                      renderCells={(row) => [
                        row.notificationId,
                        row.notificationRuleId,
                        row.deliveryMethod,
                        row.details,
                        row.scheduledSendNumber,
                        row.deliveryAttempt,
                        row.eventOccurrenceId,
                        <AdminStatusBadge key={`${row.id}-result`} status={row.result} label={row.result} />,
                        formatLoggedOnDisplay(row),
                      ]}
                    />
                  )}

                  {activeTab === TAB_INTEGRATIONS && (
                    <LogTable
                      minWidth="min-w-[1280px]"
                      columns={integrationsGrid}
                      headers={[
                        'Correlation ID',
                        'Service',
                        'Event',
                        'Details',
                        'Request Ref',
                        'Provider Ref',
                        'Environment',
                        'Result',
                        'Logged On',
                      ]}
                      rows={pageList}
                      renderCells={(row) => [
                        row.correlationId,
                        row.service,
                        row.event,
                        row.details,
                        row.requestReference,
                        row.providerReference,
                        row.environment,
                        <AdminStatusBadge key={`${row.id}-result`} status={row.result} label={row.result} />,
                        formatLoggedOnDisplay(row),
                      ]}
                    />
                  )}

                  {activeTab === TAB_API && (
                    <LogTable
                      minWidth="min-w-[1280px]"
                      columns={apiGrid}
                      headers={[
                        'Correlation ID',
                        'Service',
                        'Details',
                        'Endpoint',
                        'Request Ref',
                        'Provider Ref',
                        'Environment',
                        'Result',
                        'Logged On',
                      ]}
                      rows={pageList}
                      renderCells={(row) => [
                        row.correlationId,
                        row.service,
                        row.details,
                        row.endpoint,
                        row.requestReference,
                        row.providerReference,
                        row.environment,
                        <AdminStatusBadge key={`${row.id}-result`} status={row.result} label={row.result} />,
                        formatLoggedOnDisplay(row),
                      ]}
                    />
                  )}

                  {activeTab === TAB_HEALTH && (
                    <LogTable
                      minWidth="min-w-[1200px]"
                      columns={healthGrid}
                      headers={[
                        'Ref ID',
                        'Source',
                        'Component',
                        'Details',
                        'Fault',
                        'Environment',
                        'Result',
                        'Logged On',
                      ]}
                      rows={pageList}
                      renderCells={(row) => [
                        row.referenceId,
                        row.source,
                        row.component,
                        row.details,
                        row.fault,
                        row.environment,
                        <AdminStatusBadge key={`${row.id}-result`} status={row.result} label={row.result} />,
                        formatLoggedOnDisplay(row),
                      ]}
                    />
                  )}

                  {total === 0 && (
                    <div className="py-8 text-center text-mediumgray">
                      <AdminSupportingText>{emptyMessage}</AdminSupportingText>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={!loading && !loadError && total > 0 && showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={total}
          currentPage={currentPage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((p) => Math.max(1, p - 1))}
          onNext={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          goToPage={setCurrentPage}
        />
      </div>
    </AdminLayout>
  );
};

export default SystemLogs;
